/**
 * MentionReviewApi - API service for mention review operations
 *
 * Connects to backend endpoints:
 * - GET /api/admin/mentions/review
 * - POST /api/admin/mentions/{id}/approve
 * - POST /api/admin/mentions/{id}/reject
 *
 * Backend controller: Backend/MentionedMe.API/Controllers/Admin/MentionReviewController.cs
 */
import { apiClient } from './ApiClient';
import type {
  MentionReviewDto,
  GetMentionsForReviewParams,
  ApproveMentionRequest,
  RejectMentionRequest,
  SetMentionScopeRequest,
  AlgorithmVersionsResponse
} from '@/domain/entities/MentionReview';
import type { PagedResult } from '@/domain/entities/PagedResult';

/**
 * Base path for mention review API endpoints
 */
const BASE_PATH = '/api/admin/mentions';

/**
 * MentionReviewApi class
 *
 * Provides type-safe methods for interacting with mention review API
 */
export class MentionReviewApi {
  /**
   * Get paginated list of mentions for review
   *
   * @param params - Query parameters (filters, pagination, sorting)
   * @returns Paged result of mentions
   *
   * @example
   * const result = await mentionReviewApi.getMentionsForReview({
   *   page: 1,
   *   pageSize: 25,
   *   reviewStatus: 'Pending',
   *   sortBy: 'Confidence',
   *   sortDirection: 'Asc'
   * });
   */
  async getMentionsForReview(
    params?: GetMentionsForReviewParams
  ): Promise<PagedResult<MentionReviewDto>> {
    // Build query parameters
    const queryParams = new URLSearchParams();

    if (params?.page) {
      queryParams.append('page', params.page.toString());
    }
    if (params?.pageSize) {
      queryParams.append('pageSize', params.pageSize.toString());
    }
    if (params?.reviewStatus) {
      queryParams.append('reviewStatus', params.reviewStatus);
    }
    if (params?.minConfidence !== undefined) {
      queryParams.append('minConfidence', params.minConfidence.toString());
    }
    if (params?.maxConfidence !== undefined) {
      queryParams.append('maxConfidence', params.maxConfidence.toString());
    }
    if (params?.subredditName) {
      queryParams.append('subredditName', params.subredditName);
    }
    if (params?.businessId) {
      queryParams.append('businessId', params.businessId);
    }
    if (params?.businessNameFilter) {
      queryParams.append('businessNameFilter', params.businessNameFilter);
    }
    if (params?.regionIds && params.regionIds.length > 0) {
      params.regionIds.forEach((regionId) => {
        queryParams.append('regionIds', regionId);
      });
    }
    if (params?.algorithmVersion) {
      queryParams.append('algorithmVersion', params.algorithmVersion);
    }
    if (params?.sortBy) {
      queryParams.append('sortBy', params.sortBy);
    }
    if (params?.sortDirection) {
      queryParams.append('sortDirection', params.sortDirection);
    }

    const url = `${BASE_PATH}/review${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

    return apiClient.get<PagedResult<MentionReviewDto>>(url);
  }

  /**
   * Sprint 038: Get distinct algorithm versions for filter dropdown
   *
   * @returns List of distinct algorithm versions
   *
   * @example
   * const result = await mentionReviewApi.getAlgorithmVersions();
   * // result.versions = ['1.0.0', '1.0.1']
   */
  async getAlgorithmVersions(): Promise<AlgorithmVersionsResponse> {
    const url = `${BASE_PATH}/algorithm-versions`;
    return apiClient.get<AlgorithmVersionsResponse>(url);
  }

  /**
   * SESSION_076: Get a single mention for review by ID
   *
   * @param id - Mention ID (Guid)
   * @returns Mention with all review data
   *
   * @example
   * const mention = await mentionReviewApi.getMentionById('123e4567-e89b-12d3-a456-426614174000');
   */
  async getMentionById(id: string): Promise<MentionReviewDto> {
    const url = `${BASE_PATH}/${id}`;
    return apiClient.get<MentionReviewDto>(url);
  }

  /**
   * Approve a mention
   *
   * @param id - Mention ID (Guid)
   * @param request - Approval request with optional admin notes
   * @returns Promise<void> - Returns 204 No Content on success
   *
   * @example
   * await mentionReviewApi.approveMention('123e4567-e89b-12d3-a456-426614174000', {
   *   adminNotes: 'Verified match, good confidence'
   * });
   */
  async approveMention(id: string, request?: ApproveMentionRequest): Promise<void> {
    const url = `${BASE_PATH}/${id}/approve`;
    await apiClient.post<void>(url, request || {});
  }

  /**
   * Reject a mention
   *
   * @param id - Mention ID (Guid)
   * @param request - Rejection request with reason and optional admin notes
   * @returns Promise<void> - Returns 204 No Content on success
   *
   * @example
   * await mentionReviewApi.rejectMention('123e4567-e89b-12d3-a456-426614174000', {
   *   rejectionReason: 'WrongBusiness',
   *   adminNotes: 'Matched street name instead of business'
   * });
   */
  async rejectMention(id: string, request: RejectMentionRequest): Promise<void> {
    const url = `${BASE_PATH}/${id}/reject`;
    await apiClient.post<void>(url, request);
  }

  /**
   * Set mention scope (company vs specific location)
   *
   * @param id - Mention ID (Guid)
   * @param request - Scope request with location specificity
   * @returns Promise<void> - Returns 204 No Content on success
   *
   * @example
   * // Set to specific location
   * await mentionReviewApi.setMentionScope('123e4567-e89b-12d3-a456-426614174000', {
   *   locationSpecificity: 'Specific',
   *   specificBusinessLocationId: 12345,
   *   adminNotes: 'Customer specified Pike Street location'
   * });
   *
   * @example
   * // Set to all locations (company mention)
   * await mentionReviewApi.setMentionScope('123e4567-e89b-12d3-a456-426614174000', {
   *   locationSpecificity: 'AllLocations',
   *   adminNotes: 'Generic mention applies to all locations'
   * });
   */
  async setMentionScope(id: string, request: SetMentionScopeRequest): Promise<void> {
    const url = `${BASE_PATH}/${id}/set-scope`;
    await apiClient.post<void>(url, request);
  }

  /**
   * Trigger notifications for an already-approved mention.
   * Used after MissingContactModal creates contacts for a manual mention
   * that was auto-approved before contacts existed.
   *
   * @param id - Mention ID (Guid)
   * @param request - Business IDs to notify
   * @returns Notification result with counts
   */
  async triggerMentionNotifications(
    id: string,
    request: TriggerNotificationsRequest
  ): Promise<TriggerNotificationsResponse> {
    const url = `${BASE_PATH}/${id}/trigger-notifications`;
    return apiClient.post<TriggerNotificationsResponse>(url, request);
  }

  /**
   * Reassign a mention to a different existing business
   *
   * @param id - Mention ID (Guid)
   * @param request - Reassignment request
   * @returns Reassignment result with old and new business IDs
   */
  async reassignMentionBusiness(
    id: string,
    request: ReassignMentionBusinessRequest
  ): Promise<ReassignMentionBusinessResponse> {
    const url = `${BASE_PATH}/${id}/reassign`;
    return apiClient.post<ReassignMentionBusinessResponse>(url, request);
  }

  /**
   * Sprint 066: Bulk reassign a set of mentions from one business to another.
   * Reassign-only — does NOT change ReviewStatus or fire notifications.
   * Returns per-item errors without rolling back successful items.
   *
   * @param request - Bulk reassignment request (sourceBusinessId, newBusinessId, mentionIds[])
   * @returns Batch result with counts and per-item errors
   */
  async bulkReassignMentions(
    request: BulkReassignMentionsRequest
  ): Promise<BulkReassignMentionsResponse> {
    const url = `${BASE_PATH}/bulk-reassign`;
    return apiClient.post<BulkReassignMentionsResponse>(url, request);
  }

  /**
   * Create a new business and reassign a mention to it
   *
   * @param id - Mention ID (Guid)
   * @param request - Business creation + reassignment request
   * @returns Result with new business ID and company ID
   */
  async createBusinessAndReassign(
    id: string,
    request: CreateBusinessAndReassignRequest
  ): Promise<CreateBusinessAndReassignResponse> {
    const url = `${BASE_PATH}/${id}/create-business-and-reassign`;
    return apiClient.post<CreateBusinessAndReassignResponse>(url, request);
  }
}

/**
 * Request/Response types for mention reassignment
 */
export interface ReassignMentionBusinessRequest {
  newBusinessId: string;
  adminNotes?: string;
  mentionText?: string;
  alsoApprove: boolean;
  emotionalTone?: 'Positive' | 'Negative' | 'Neutral';
}

export interface ReassignMentionBusinessResponse {
  success: boolean;
  message: string;
  mentionId: string;
  newBusinessId: string;
  originalBusinessId: string | null;
}

/**
 * Request/Response types for bulk mention reassignment (Sprint 066)
 * Reassign-only — does NOT touch ReviewStatus or trigger notifications.
 *
 * Matches: Backend/MentionedMe.Application/Features/RedditMentions/Commands/BulkReassignMentions/
 */
export interface BulkReassignMentionsRequest {
  newBusinessId: string;
  sourceBusinessId: string;
  mentionIds: string[];
  adminNotes?: string;
}

export interface BulkReassignMentionError {
  mentionId: string;
  reason: string;
}

export interface BulkReassignMentionsResponse {
  success: boolean;
  message: string;
  bulkReassignmentBatchId: string;
  reassignedCount: number;
  failedCount: number;
  reviewedMentionCount: number;
  errors: BulkReassignMentionError[];
}

export interface CreateBusinessAndReassignRequest {
  name: string;
  category: string;
  email?: string;
  phone?: string;
  website?: string;
  houseNumber?: string;
  street?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  regionId: string;
  subAreaId?: string;
  adminNotes?: string;
  mentionText?: string;
  alsoApprove: boolean;
  emotionalTone?: 'Positive' | 'Negative' | 'Neutral';
}

export interface CreateBusinessAndReassignResponse {
  success: boolean;
  message: string;
  newBusinessId: string;
  companyId: string;
  mentionId: string;
  originalBusinessId: string | null;
}

export interface TriggerNotificationsRequest {
  businessIds: string[];
}

export interface TriggerNotificationsResponse {
  success: boolean;
  contactsProcessed: number;
  notificationsSent: number;
  notificationsSkipped: number;
  errors: string[];
}

/**
 * Default mention review API instance
 */
export const mentionReviewApi = new MentionReviewApi();
